// src/config/roles.js
//
// Single source of truth for system roles. Everything that gates a route, a
// sidebar link or a button imports from here — never re-type the strings, or a
// rename silently grants (or revokes) access somewhere else.
//
// A role lives on the user record in Realtime DB at `users/{uid}.role`.
// An empty/missing role means a regular employee: no admin module access.

export const ROLES = {
  ADMIN: 'Admin',
  IT_ADMIN: 'IT Admin',
  HR_ADMIN: 'HR Admin',
  PAYROLL_ADMIN: 'Payroll Admin',
  ASSETS_ADMIN: 'Assets Admin',
};

export const ALL_ROLES = Object.values(ROLES);

// Historic / hand-typed spellings that should resolve to a canonical role.
// Keys are compared after lowercasing and collapsing _ - and whitespace.
const ALIASES = {
  [ROLES.ADMIN]: ['admin', 'administrator'],
  [ROLES.IT_ADMIN]: ['it admin', 'itadmin', 'it'],
  [ROLES.HR_ADMIN]: ['hr admin', 'hradmin', 'hr'],
  [ROLES.PAYROLL_ADMIN]: ['payroll admin', 'payrolladmin', 'payroll'],
  [ROLES.ASSETS_ADMIN]: ['assets admin', 'assetsadmin', 'asset admin', 'assets'],
};

const keyOf = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const CANONICAL_BY_KEY = new Map();
ALL_ROLES.forEach((role) => {
  CANONICAL_BY_KEY.set(keyOf(role), role);
  (ALIASES[role] || []).forEach((alias) => CANONICAL_BY_KEY.set(keyOf(alias), role));
});

/**
 * Resolve a stored role value to one of ROLES.
 * Returns null for empty values and for anything we don't recognise, so an
 * unknown string can never accidentally match a permission list.
 */
export const normalizeRole = (role) => CANONICAL_BY_KEY.get(keyOf(role)) ?? null;

/** True when the stored value is a role the system knows how to act on. */
export const isKnownRole = (role) => normalizeRole(role) !== null;

/**
 * True when `role` is in `allowedRoles`. Comparison is normalized, so casing
 * and legacy spellings ("hr_admin") still match.
 * An empty `allowedRoles` means "no role restriction" and always passes.
 */
export const hasRole = (role, allowedRoles) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const current = normalizeRole(role);
  if (!current) return false;
  return allowedRoles.some((allowed) => normalizeRole(allowed) === current);
};

// ---------------------------------------------------------------------------
// Access lists — the only place a "who can do X" decision is written down.
// ---------------------------------------------------------------------------

/** Full access to every module. */
export const SUPER_ADMINS = [ROLES.IT_ADMIN, ROLES.ADMIN];

export const CAN_ACCESS_ASSETS = [ROLES.ASSETS_ADMIN, ...SUPER_ADMINS];
export const CAN_ACCESS_HR = [ROLES.HR_ADMIN, ...SUPER_ADMINS];
export const CAN_ACCESS_PAYROLL = [ROLES.PAYROLL_ADMIN, ROLES.HR_ADMIN, ...SUPER_ADMINS];
export const CAN_ACCESS_TICKETING_ADMIN = [...SUPER_ADMINS];

/** May be picked as the agent a ticket is assigned to. */
export const CAN_BE_ASSIGNED_TICKET = [ROLES.IT_ADMIN];
export const CAN_ACCESS_SYSDEV = [...SUPER_ADMINS];

export const CAN_ANNOUNCE = [ROLES.HR_ADMIN, ...SUPER_ADMINS];
export const CAN_MANAGE_RESOURCES = [ROLES.HR_ADMIN, ...SUPER_ADMINS];

/** Sees org-wide attendance on the calendar rather than just their own. */
export const CAN_VIEW_ORG_CALENDAR = [ROLES.HR_ADMIN, ...SUPER_ADMINS];

/** May change another user's role. Kept deliberately narrow. */
export const CAN_ASSIGN_ROLES = [...SUPER_ADMINS];

/** Options for a role <Select>. The blank value is a regular employee. */
export const ROLE_OPTIONS = [
  { value: '', label: 'Employee (no admin access)' },
  ...ALL_ROLES.map((role) => ({ value: role, label: role })),
];
